<template>
	<div class="steps__step-form">
		<h3 class="h4 h4--b">
			Please provide the most accurate information available regarding the project deliverables
		</h3>
		<p class="mb-24">
			All fields are required unless indicated as optional.
		</p>
		<form
			class="form form--fixed-fields"
			@submit.prevent="updateForm"
		>
			<div class="form__set form__set--wrap">
				<InputComponent
					:value="form_fields.billing_location"
					type="select"
					input-id="billing-location"
					name="billing-location"
					label="Billing Location"
					:options="billing_locations"
					:required="true"
					:errors="form_errors.billing_location || null"
					@update:value="new_value => form_fields.billing_location = new_value"
				/>
				<InputComponent
					:value="form_fields.production_office"
					type="select"
					input-id="production-office"
					name="production-office"
					label="Production Office"
					:options="offices"
					:required="true"
					:errors="form_errors.production_office || null"
					@update:value="new_value => form_fields.production_office = new_value"
				/>
				<InputComponent
					:value="form_fields.currency"
					type="select"
					input-id="currency"
					name="currency"
					label="Currency"
					:options="currencies"
					:required="true"
					:errors="form_errors.currency || null"
					@update:value="new_value => form_fields.currency = new_value"
				/>
			</div>


			<template v-if="scope">
				<h4 class="h5 h5--b">
					Project Deliverables
				</h4>
				<h3 class="h5">
					<span v-if="scope.sequence_prefix">
						{{ scope.sequence_prefix }}&nbsp;
					</span>
					<span
						v-if="scope.sequence_prefix"
						class="light"
					>
						&mdash;&nbsp;
					</span>
					<span class="light">
						{{ scope.title }}
					</span>
				</h3>

				<ProjectDeliverableComponent
					v-for="item, index in scope.items"
					:key="'scope-item' + index"
					:deliverable="item"
					:currency="selected_currency"
				/>

				<div class="mb-32">
					<p class="h5">
						<span class="light">Total project cost:</span> {{ total }}
					</p>
				</div>
			</template>

			<button
				class="button button--fill"
				type="submit"
			>
				Save {{ stepTitle }}
			</button>
		</form>
	</div>
</template>

<script setup>
import { useNewProjectStore } from '../../stores/new-project.js';
import { ref, computed, defineProps, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { formatCurrency } from '../../../helpers';
import InputComponent from '../InputComponent';
import ProjectDeliverableComponent from '../ProjectDeliverableComponent';

const new_project_store = useNewProjectStore();
const {
	getBillingLocations,
	getOffices,
	getCurrencies,
	validateNewProjectStep
} = new_project_store;
const {
	form_fields,
	form_errors,
	billing_locations,
	offices,
	currencies
} = storeToRefs( new_project_store );

defineProps( {
	stepTitle: {
		required: true,
		type: String,
		default: ''
	}
} );

const loading = ref( true );

onMounted ( async() => {
	await getBillingLocations();
	await getOffices();
	await getCurrencies();

	loading.value = false;
} );

const selected_currency = computed( () => {
	return currencies.value.find( currency => parseInt( currency.value ) === parseInt( form_fields.value.currency ) );
} );

async function updateForm() {
	validateNewProjectStep();
}

const scope = computed( () => {
	return form_fields.value.project_scope ?? null;
} );

const total = computed( () => {
	if ( !form_fields.value.project_scope || !form_fields.value.project_scope.items?.length ) {
		return null;
	}
	const total_cost = form_fields.value.project_scope.items.reduce( ( acc, val ) => acc + val.total, 0 );
	return formatCurrency( total_cost, selected_currency.value.label )
} );

</script>
